'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { sendViewItemEvent } from '@/features/analytics/datalayer/datalayer';
import { ViewItemType } from '@/features/analytics/datalayer/types';
import { WindowWithDataLayer } from '@/features/analytics/types';
import InteractifyCarouselSkeleton from '@/features/interactify/carousel/skeleton';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { handleHidePageLoading } from '@/features/page-loading/utils/show-loading';
import { Webengage } from '@/features/webengage';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import Gift from './gift';
import PDPBuyingPolicies from './pdp-buying-policies';
import PDPCta from './pdp-cta';
import PdpCtaSkeleton from './pdp-cta-skeleton';
import ProductStats from './product-stats';
import RecomendationSkeleton from './recomendation-skeleton';
import SimilarsSkeleton from './similars-skeleton';
import PDPSkeleton from './skeleton';
import VendorDetails from './vendor-details';
import Breadcrumb from './breadcrumb/breadcrumb';
import WishlistButtonSkeleton from './buttons/wishlist-button/skeleton';
import BuyBox from './buy-box/index';
import Gallery from './gallery/gallery';
import Description from './product-details/description';
import HowToUse from './product-details/how-to-use';
import ProductDetails from './product-details/product-details';
import UGCSkeleton from './ugc-get/skeleton';
import usePDPData from '../_utils/use-pdp-data';
import usePDPInitialLoading from '../_utils/use-pdp-initial-loading';
import PDPPageVariantProvider from '../context/pdp-page-variant-context/provider';
import Schemas from '../schemas';
const ShareButton = dynamic(() => import('./buttons/share/share-button'), {
  ssr: false,
  loading: () => <WishlistButtonSkeleton />
});
const WishlistButton = dynamic(() => import('./buttons/wishlist-button/wishlist-button'), {
  ssr: false,
  loading: () => <WishlistButtonSkeleton />
});
const SponsoredAds = dynamic(() => import('./sponsored-ads'), {
  ssr: false,
  loading: () => <SimilarsSkeleton />
});
const InteractifyCarousel = dynamic(() => import('@/features/interactify/carousel/index'), {
  ssr: false,
  loading: () => <InteractifyCarouselSkeleton className='pdp-container mt-4 overflow-hidden lg:mt-7' />
});
const PDPAlsoBought = dynamic(() => import('./pdp-also-bought'), {
  ssr: PUBLIC_IS_BOT,
  loading: () => <RecomendationSkeleton />
});
const Similars = dynamic(() => import('./similars'), {
  ssr: PUBLIC_IS_BOT,
  loading: () => <SimilarsSkeleton />
});
const Complementaries = dynamic(() => import('./complementaries'), {
  ssr: PUBLIC_IS_BOT,
  loading: () => <RecomendationSkeleton />
});
const CommentsSection = dynamic(() => import('./ugc-get/comments-section'), {
  ssr: PUBLIC_IS_BOT,
  loading: () => <UGCSkeleton />
});
declare const window: WindowWithDataLayer;
interface Props {
  slug: string;
}
const PDPContent = ({
  slug
}: Props) => {
  const {
    isMobile
  } = useMobileDetect();
  const {
    data
  } = usePDPData();
  const isLoading = usePDPInitialLoading({
    slug
  });
  useEffect(() => {
    if (isLoading) return;
    const intervalID = setInterval(() => {
      if (!window.dataLayer || !data) return;
      clearInterval(intervalID);
      sendViewItemEvent(data, ViewItemType.PDP);
    }, 500);
    return () => clearInterval(intervalID);
  }, [isLoading]); // eslint-disable-line

  useEffect(() => {
    if (!isLoading) handleHidePageLoading();
  }, [isLoading]);
  useEffect(() => {
    if (!data) return;
    Webengage.trackViewPdp(data, ViewItemType.PDP);
  }, [data]);
  return <PDPPageVariantProvider product={data} isLoading={isLoading} data-sentry-element="PDPPageVariantProvider" data-sentry-component="PDPContent" data-sentry-source-file="pdp-content.tsx">
            {isLoading && !PUBLIC_IS_BOT && <PDPSkeleton />}

            <Schemas data-sentry-element="Schemas" data-sentry-source-file="pdp-content.tsx" />

            <div className={classNames({
      'px-2': isLoading
    })}>
                <Breadcrumb data-sentry-element="Breadcrumb" data-sentry-source-file="pdp-content.tsx" />

                <div className='pdp-container flex flex-col lg:flex-row lg:gap-8'>
                    <div className='flex flex-col lg:gap-3'>
                        <Gallery data-sentry-element="Gallery" data-sentry-source-file="pdp-content.tsx" />
                        {!isMobile && <ProductStats />}
                    </div>

                    <div className='flex grow flex-col lg:flex-row lg:gap-8'>
                        <ProductDetails data-sentry-element="ProductDetails" data-sentry-source-file="pdp-content.tsx" />

                        <div className='hidden w-[316px] flex-col gap-2 lg:flex'>
                            <div className='mr-auto flex gap-3'>
                                <WishlistButton data-sentry-element="WishlistButton" data-sentry-source-file="pdp-content.tsx" />
                                <ShareButton data-sentry-element="ShareButton" data-sentry-source-file="pdp-content.tsx" />
                            </div>

                            <BuyBox hasPhoto={false} data-sentry-element="BuyBox" data-sentry-source-file="pdp-content.tsx" />
                            <Gift data-sentry-element="Gift" data-sentry-source-file="pdp-content.tsx" />
                        </div>
                    </div>
                </div>

                <Gift className='mt-3 flex lg:hidden' data-sentry-element="Gift" data-sentry-source-file="pdp-content.tsx" />

                {isMobile && <VendorDetails mode='mobile' />}

                <PDPBuyingPolicies type='mobile' data-sentry-element="PDPBuyingPolicies" data-sentry-source-file="pdp-content.tsx" />

                <div className='pdp-container flex flex-col'>
                    <Description data-sentry-element="Description" data-sentry-source-file="pdp-content.tsx" />

                    <HowToUse isOpen={false} data-sentry-element="HowToUse" data-sentry-source-file="pdp-content.tsx" />
                </div>

                <Complementaries slug={slug} data-sentry-element="Complementaries" data-sentry-source-file="pdp-content.tsx" />

                <Similars slug={slug} title='محصولات مشابه' data-sentry-element="Similars" data-sentry-source-file="pdp-content.tsx" />

                <PDPAlsoBought slug={slug} data-sentry-element="PDPAlsoBought" data-sentry-source-file="pdp-content.tsx" />

                <SponsoredAds id={data?.id ?? ''} title='از همین دسته‌بندی' data-sentry-element="SponsoredAds" data-sentry-source-file="pdp-content.tsx" />

                <InteractifyCarousel productSlug={slug} title='ویدئوهای بررسی این کالا' className='pdp-container mt-4 lg:mt-7' withInfiniteLoading data-sentry-element="InteractifyCarousel" data-sentry-source-file="pdp-content.tsx" />

                <div className='pdp-container my-6 flex gap-8 lg:mb-0'>
                    <CommentsSection data-sentry-element="CommentsSection" data-sentry-source-file="pdp-content.tsx" />

                    <div className='hidden lg:block'>
                        <div className='sticky top-34 flex w-[316px] flex-col gap-2'>
                            <BuyBox hasPhoto={true} className='mb-2' data-sentry-element="BuyBox" data-sentry-source-file="pdp-content.tsx" />
                            <Gift data-sentry-element="Gift" data-sentry-source-file="pdp-content.tsx" />
                        </div>
                    </div>
                </div>

                <PDPCta data-sentry-element="PDPCta" data-sentry-source-file="pdp-content.tsx" />

                {isLoading && !PUBLIC_IS_BOT ? <PdpCtaSkeleton /> : null}
            </div>
        </PDPPageVariantProvider>;
};
export default PDPContent;